/**
 * Carries "I typed {slug}.{rootDomain} directly" across the identity-login
 * detour to the fixed base URL.
 *
 * Auth0 is only ever invoked from ONE fixed origin (VITE_APP_BASE_URL) —
 * never from a workspace subdomain directly — so its Allowed Callback URLs
 * list needs exactly one entry, no wildcard, no per-workspace maintenance.
 * When someone lands on {slug}.{rootDomain} unauthenticated, LoginPage
 * bounces them to the base URL's /login carrying `?returnSlug=` in the
 * query string; the base LoginPage stashes it here (sessionStorage, so it
 * survives the full-page round trip through Auth0) before starting the
 * normal identity login. Once a valid backend JWT exists, WorkspaceGate
 * consumes the stash and hands the JWT off to the originally-requested
 * subdomain (see tokenHandoff.js) instead of applying its usual
 * "land on the token's own default workspace" decision.
 */

const KEY = 'aims_pending_workspace_redirect';

/**
 * @param {{ slug: string, path?: string }} params
 */
export function stashPendingWorkspaceRedirect({ slug, path }) {
  if (typeof window === 'undefined' || !slug) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ slug, path: path || '/' }));
  } catch {
    // sessionStorage can throw in private mode — the user just lands on
    // their default workspace instead of the one they originally typed.
  }
}

/**
 * Reads and clears the stash in one step.
 *
 * @returns {{ slug: string, path: string } | null}
 */
export function consumePendingWorkspaceRedirect() {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(KEY);
    const parsed = JSON.parse(raw);
    if (!parsed?.slug) return null;
    return parsed;
  } catch {
    return null;
  }
}
